/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
    ResponsiveContainer,
    ComposedChart,
    XAxis,
    YAxis,
    Tooltip,
    Scatter,
    ErrorBar,
    ReferenceLine
} from 'recharts';
import { selectAllTopSections } from "../../slices/topSections/topSectionsSlice.js";
import { selectColorScheme } from "../../slices/colorScheme/colorSchemeSlice.js";
import { selectAllNetEffectContributions } from "../../slices/netEffectContributions/netEffectContributionsSlice.js";
import getNetEffectEstimate from "../compositionResourceFunctions/getNetEffectEstimate.js";

const parseValuesFromDiv = (htmlString) => {
    if (!htmlString) return null;
    const regex = />(-?[\d.]+)</g;
    const matches = [...htmlString.matchAll(regex)];
    if (matches.length < 3) return null;
    const [point, lower, upper] = matches.map(match => parseFloat(match[1]));
    return { point, lower, upper };
};

const CustomShape = (props) => {
    const { cx, cy, payload, dataKey, green, red } = props;
    const pointValue = payload[dataKey];
    const color = pointValue > 0 ? green : pointValue < 0 ? red : 'black';

    if (payload.type === 'overall') {
        const size = 14;
        return <path d={`M${cx},${cy - size} L${cx + size},${cy} L${cx},${cy + size} L${cx - size},${cy} Z`} fill={color} />;
    } else {
        const size = 12;
        return <rect x={cx - size / 2} y={cy - size / 2} width={size} height={size} fill={color} />;
    }
};

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;

        const point = data.point1;
        const lower = data.lower1;
        const upper = data.upper1;

        if (!point && point !== 0) return null;

        return (
            <div style={{
                backgroundColor: 'white', border: '1px solid #ccc',
                padding: '4px', lineHeight: '1.2'
            }}>
                <p style={{ margin: 0, fontWeight: 'bold' }}>{label}</p>
                <p style={{ margin: 0, color: '#333' }}>{point.toFixed(2)} ({lower.toFixed(2)} to {upper.toFixed(2)})</p> {/* Point Estimate: */}
                {/* <p style={{ margin: 0, color: '#666' }}>Confidence Interval: ({lower.toFixed(2)} to {upper.toFixed(2)})</p> */}
            </div>
        );
    }
    return null;
};

//So it has a bigger hover area than what is visible
const CustomErrorBar = ({dataKey, width, stroke, direction, strokeWidth}) => {
    return <><ErrorBar dataKey={dataKey} width={width} stroke={stroke} direction={direction} strokeWidth={strokeWidth} /><ErrorBar dataKey={dataKey} width={width} stroke="transparent" direction={direction} strokeWidth={20} /></>;
}

//TODO have HealthImpactForestPlotEnhanced and HealthImpactForestPlotEnhancedForUser be one component instead of two
const HealthImpactForestPlotEnhancedCommon = ({ }) => {

}

const HealthImpactForestPlotEnhanced = ({ findingsSectionIndex, netEffectEstimateSectionIndex }) => {
    const allTopSections = useSelector(selectAllTopSections);
    const netEffectContributions = useSelector(state => selectAllNetEffectContributions(state));
    const colorScheme = useSelector(selectColorScheme);
    const [green, setGreen] = useState("green");
    const [red, setRed] = useState("red");

    useEffect(() => {
        if (colorScheme === "red-green") {
            setGreen("green");
            setRed("red");
        } else if (colorScheme === "orange-blue") {
            setGreen("#0033AAFF");//setGreen("blue");
            setRed("#AA6000FF"); //setRed("orange");
        } else if (colorScheme === "greyscale") {
            setGreen("#5E5E5E");
            setRed("#AEAEAE");
        }
    }, [colorScheme]);

    const processedData = useMemo(() => {
        const sections = allTopSections.map(entity => entity.revisedSection);
        const netEffectEstimate = getNetEffectEstimate({ section: sections }) || null;
        let initialData = [];
        if (netEffectContributions) {
            initialData = netEffectContributions.map(row => {
                try {
                    const label = row?.title || 'Unknown Outcome';
                    const lastCell = row.section[row.section.length - 1];
                    const values = parseValuesFromDiv(lastCell?.text?.div);
                    if (!values) return null;
                    return { ...values, label, type: 'individual' };
                } catch (e) { return null; }
            }).filter(Boolean);
        }
        if (netEffectEstimate) {
            try {
                const label = netEffectEstimate?.title || 'Overall Estimate'; //TODO - check if netEffectEstimate is a string value or object
                const values = parseValuesFromDiv(netEffectEstimate);
                if (values) {
                    initialData.push({ ...values, label, type: 'overall' });
                }
            } catch (error) { console.error("Failed to parse Net Effect Estimate for plot:", error); }
        }
        if (initialData.length === 0) {
            return { plotData: [], domain: [0, 0] };
        }

        const multiSeriesData = initialData.map(row => {
            let point2 = null, lower2 = null, upper2 = null, error2 = null;
            let point3 = null, lower3 = null, upper3 = null, error3 = null;
            let point4 = null, lower4 = null, upper4 = null, error4 = null;
            let point5 = null, lower5 = null, upper5 = null, error5 = null;
            let point6 = null, lower6 = null, upper6 = null, error6 = null;
            let point7 = null, lower7 = null, upper7 = null, error7 = null;
            let point8 = null, lower8 = null, upper8 = null, error8 = null;
            if (row.lower < 0 && row.upper > 0) {   //lower and upper are typeof number, if they are null (I didn't see any) it's treated as 0 for comparator
                // Series 2 (Negative Segment: lower to 0)
                const negLower = row.lower;
                const negUpper = 0;
                point2 = (negLower + negUpper) / 2;
                lower2 = negLower;
                upper2 = negUpper;
                error2 = [point2 - lower2, upper2 - point2];

                // Series 3 (Positive Segment: 0 to upper)
                const posLower = 0;
                const posUpper = row.upper;
                point3 = (posLower + posUpper) / 2;
                lower3 = posLower;
                upper3 = posUpper;
                error3 = [point3 - lower3, upper3 - point3];

                // Series 6 (Zero-length green whisker at upper bound)
                const seg6Lower = row.upper;
                const seg6Upper = row.upper;
                point6 = seg6Upper;
                lower6 = seg6Lower;
                upper6 = seg6Upper;
                error6 = [0, 0];

                // Series 7 (Zero-length red whisker at lower bound)
                const seg7Lower = row.lower;
                const seg7Upper = row.lower;
                point7 = seg7Lower;
                lower7 = seg7Lower;
                upper7 = seg7Upper;
                error7 = [0, 0];
            }
            else if (row.lower >= 0) {
                // Series 4 (Full green line for positive CIs)
                point4 = row.point;
                lower4 = row.lower;
                upper4 = row.upper;
                error4 = [point4 - lower4, upper4 - point4];
            }
            else if (row.upper <= 0) {
                // Series 5 (Full red line for negative CIs)
                point5 = row.point;
                lower5 = row.lower;
                upper5 = row.upper;
                error5 = [point5 - lower5, upper5 - point5];
            }

            if (row.lower === 0 || row.upper === 0) {
                // Series 8 (Black whisker at zero if an interval touches it)
                point8 = 0;
                lower8 = 0;
                upper8 = 0;
                error8 = [0, 0];
            }

            return {
                label: row.label, type: row.type,
                point1: row.point, lower1: row.lower, upper1: row.upper,
                point2, lower2, upper2, error2,
                point3, lower3, upper3, error3,
                point4, lower4, upper4, error4,
                point5, lower5, upper5, error5,
                point6, lower6, upper6, error6,
                point7, lower7, upper7, error7,
                point8, lower8, upper8, error8,
            };
        });

        let dataMin = Infinity;
        let dataMax = -Infinity;
        multiSeriesData.forEach(entry => {
            if (entry.lower1 < dataMin) dataMin = entry.lower1;
            if (entry.upper1 > dataMax) dataMax = entry.upper1;
        });
        const domain = [Math.floor(dataMin) - 2, Math.ceil(dataMax) + 2];

        return { plotData: multiSeriesData, domain: domain };
    }, [netEffectContributions, allTopSections]);

    if (processedData.plotData.length === 0) {
        return <></>;
    }
    const plotHeight = Math.max(300, 60 * netEffectContributions.length);


    return <>
        <>
            <br />
            <div style={{ width: '100%', height: plotHeight }}>
                <h3 style={{ margin: "0px", padding: "0px" }}>Health Impact Forest Plot</h3>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        className="forest-plot-chart"
                        layout="vertical"
                        data={processedData.plotData}
                        margin={{ top: 20, right: 50, left: 40, bottom: 20 }}
                    >
                        <XAxis type="number" allowDataOverflow={true} domain={processedData.domain} tickCount={10} />
                        <YAxis dataKey="label" type="category" scale="point" padding={{ top: 20, bottom: 20 }} width={230} tick={{ fontSize: 16 }} />
                        <Tooltip content={<CustomTooltip />} cursor={false} />
                        <ReferenceLine x={0} stroke="black" strokeDasharray="3 3" />

                        <Scatter name="Series 2" dataKey="point2" fill="transparent" isAnimationActive={false}><CustomErrorBar dataKey="error2" width={0} stroke={red} direction="x" strokeWidth={2} /></Scatter>
                        <Scatter name="Series 3" dataKey="point3" fill="transparent" isAnimationActive={false}><CustomErrorBar dataKey="error3" width={0} stroke={green} direction="x" strokeWidth={2} /></Scatter>
                        <Scatter name="Series 4" dataKey="point4" fill="transparent" isAnimationActive={false}><CustomErrorBar dataKey="error4" width={6} stroke={green} direction="x" strokeWidth={2} /></Scatter>
                        <Scatter name="Series 5" dataKey="point5" fill="transparent" isAnimationActive={false}><CustomErrorBar dataKey="error5" width={6} stroke={red} direction="x" strokeWidth={2} /></Scatter>
                        <Scatter name="Series 6" dataKey="point6" fill="transparent" isAnimationActive={false}><CustomErrorBar dataKey="error6" width={6} stroke={green} direction="x" strokeWidth={2} /></Scatter>
                        <Scatter name="Series 7" dataKey="point7" fill="transparent" isAnimationActive={false}><CustomErrorBar dataKey="error7" width={6} stroke={red} direction="x" strokeWidth={2} /></Scatter>
                        <Scatter name="Series 8" dataKey="point8" fill="transparent" isAnimationActive={false}><CustomErrorBar dataKey="error8" width={6} stroke="black" direction="x" strokeWidth={2} /></Scatter>

                        {/* The main point estimate, drawn on top */}
                        <Scatter name="Series 1" dataKey="point1" isAnimationActive={false} shape={<CustomShape dataKey="point1" green={green} red={red} />} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
            <br />
            <br />
        </>
    </>
}

const HealthImpactForestPlotEnhancedForUser = ({ netEffectEstimateSection }) => {
    const netEffectContributions = useSelector(state => selectAllNetEffectContributions(state));
    const colorScheme = useSelector(selectColorScheme);
    const [green, setGreen] = useState("green");
    const [red, setRed] = useState("red");

    useEffect(() => {
        if (colorScheme === "red-green") {
            setGreen("green");
            setRed("red");
        } else if (colorScheme === "orange-blue") {
            setGreen("#0033AAFF");//setGreen("blue");
            setRed("#AA6000FF"); //setRed("orange");
        } else if (colorScheme === "greyscale") {
            setGreen("#5E5E5E");
            setRed("#AEAEAE");
        }
    }, [colorScheme]);

    const processedData = useMemo(() => {
        let initialData = [];
        if (netEffectContributions) {
            initialData = netEffectContributions.map(row => {
                try {
                    const label = row?.title || 'Unknown Outcome';
                    const lastCell = row.section[row.section.length - 1];
                    const values = parseValuesFromDiv(lastCell?.text?.div);
                    if (!values) return null;
                    return { ...values, label, type: 'individual' };
                } catch (e) { return null; }
            }).filter(Boolean);
        }
        if (netEffectEstimateSection) {
            try {
                const label = netEffectEstimateSection?.title || 'Overall Estimate';
                const values = parseValuesFromDiv(netEffectEstimateSection.text?.div);
                if (values) {
                    initialData.push({ ...values, label, type: 'overall' });
                }
            } catch (error) { console.error("Failed to parse Net Effect Estimate for plot:", error); }
        }
        if (initialData.length === 0) {
            return { plotData: [], domain: [0, 0] };
        }

        const multiSeriesData = initialData.map(row => {
            let point2 = null, lower2 = null, upper2 = null, error2 = null;
            let point3 = null, lower3 = null, upper3 = null, error3 = null;
            let point4 = null, lower4 = null, upper4 = null, error4 = null;
            let point5 = null, lower5 = null, upper5 = null, error5 = null;
            let point6 = null, lower6 = null, upper6 = null, error6 = null;
            let point7 = null, lower7 = null, upper7 = null, error7 = null;
            let point8 = null, lower8 = null, upper8 = null, error8 = null;
            if (row.lower < 0 && row.upper > 0) {   //lower and upper are typeof number, if they are null (I didn't see any) it's treated as 0 for comparator
                // Series 2 (Negative Segment: lower to 0)
                const negLower = row.lower;
                const negUpper = 0;
                point2 = (negLower + negUpper) / 2;
                lower2 = negLower;
                upper2 = negUpper;
                error2 = [point2 - lower2, upper2 - point2];

                // Series 3 (Positive Segment: 0 to upper)
                const posLower = 0;
                const posUpper = row.upper;
                point3 = (posLower + posUpper) / 2;
                lower3 = posLower;
                upper3 = posUpper;
                error3 = [point3 - lower3, upper3 - point3];

                // Series 6 (Zero-length green whisker at upper bound)
                const seg6Lower = row.upper;
                const seg6Upper = row.upper;
                point6 = seg6Upper;
                lower6 = seg6Lower;
                upper6 = seg6Upper;
                error6 = [0, 0];

                // Series 7 (Zero-length red whisker at lower bound)
                const seg7Lower = row.lower;
                const seg7Upper = row.lower;
                point7 = seg7Lower;
                lower7 = seg7Lower;
                upper7 = seg7Upper;
                error7 = [0, 0];
            }
            else if (row.lower >= 0) {
                // Series 4 (Full green line for positive CIs)
                point4 = row.point;
                lower4 = row.lower;
                upper4 = row.upper;
                error4 = [point4 - lower4, upper4 - point4];
            }
            else if (row.upper <= 0) {
                // Series 5 (Full red line for negative CIs)
                point5 = row.point;
                lower5 = row.lower;
                upper5 = row.upper;
                error5 = [point5 - lower5, upper5 - point5];
            }

            if (row.lower === 0 || row.upper === 0) {
                // Series 8 (Black whisker at zero if an interval touches it)
                point8 = 0;
                lower8 = 0;
                upper8 = 0;
                error8 = [0, 0];
            }

            return {
                label: row.label, type: row.type,
                point1: row.point, lower1: row.lower, upper1: row.upper,
                point2, lower2, upper2, error2,
                point3, lower3, upper3, error3,
                point4, lower4, upper4, error4,
                point5, lower5, upper5, error5,
                point6, lower6, upper6, error6,
                point7, lower7, upper7, error7,
                point8, lower8, upper8, error8,
            };
        });

        let dataMin = Infinity;
        let dataMax = -Infinity;
        multiSeriesData.forEach(entry => {
            if (entry.lower1 < dataMin) dataMin = entry.lower1;
            if (entry.upper1 > dataMax) dataMax = entry.upper1;
        });
        const domain = [Math.floor(dataMin) - 2, Math.ceil(dataMax) + 2];

        return { plotData: multiSeriesData, domain: domain };
    }, [netEffectContributions]);

    if (processedData.plotData.length === 0) {
        return <></>;
    }
    const plotHeight = Math.max(300, 60 * netEffectContributions.length);


    return <>
        <>
            <br />
            <div style={{ width: '100%', height: plotHeight }}>
                <h3 style={{ margin: "0px", padding: "0px" }}>Health Impact Forest Plot</h3>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        className="forest-plot-chart"
                        layout="vertical"
                        data={processedData.plotData}
                        margin={{ top: 20, right: 50, left: 40, bottom: 20 }}
                    >
                        <XAxis type="number" allowDataOverflow={true} domain={processedData.domain} tickCount={10} />
                        <YAxis dataKey="label" type="category" scale="point" padding={{ top: 20, bottom: 20 }} width={230} tick={{ fontSize: 16 }} />
                        <Tooltip content={<CustomTooltip />} cursor={false} />
                        <ReferenceLine x={0} stroke="black" strokeDasharray="3 3" />

                        <Scatter name="Series 2" dataKey="point2" fill="transparent" isAnimationActive={false}><CustomErrorBar dataKey="error2" width={0} stroke={red} direction="x" strokeWidth={2} /></Scatter>
                        <Scatter name="Series 3" dataKey="point3" fill="transparent" isAnimationActive={false}><CustomErrorBar dataKey="error3" width={0} stroke={green} direction="x" strokeWidth={2} /></Scatter>
                        <Scatter name="Series 4" dataKey="point4" fill="transparent" isAnimationActive={false}><CustomErrorBar dataKey="error4" width={6} stroke={green} direction="x" strokeWidth={2} /></Scatter>
                        <Scatter name="Series 5" dataKey="point5" fill="transparent" isAnimationActive={false}><CustomErrorBar dataKey="error5" width={6} stroke={red} direction="x" strokeWidth={2} /></Scatter>
                        <Scatter name="Series 6" dataKey="point6" fill="transparent" isAnimationActive={false}><CustomErrorBar dataKey="error6" width={6} stroke={green} direction="x" strokeWidth={2} /></Scatter>
                        <Scatter name="Series 7" dataKey="point7" fill="transparent" isAnimationActive={false}><CustomErrorBar dataKey="error7" width={6} stroke={red} direction="x" strokeWidth={2} /></Scatter>
                        <Scatter name="Series 8" dataKey="point8" fill="transparent" isAnimationActive={false}><CustomErrorBar dataKey="error8" width={6} stroke="black" direction="x" strokeWidth={2} /></Scatter>

                        {/* The main point estimate, drawn on top */}
                        <Scatter name="Series 1" dataKey="point1" isAnimationActive={false} shape={<CustomShape dataKey="point1" green={green} red={red} />} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
            <br />
            <br />
        </>
    </>
}

export default HealthImpactForestPlotEnhanced;
export { HealthImpactForestPlotEnhancedForUser };